/**
 * Plantilla del ANEXO 6 (Estado de Excedentes o Pérdidas Condensado). El encabezado
 * (filas 1-12) se arma en generateAnexo6() con el mismo formato que el resto de
 * anexos; esta plantilla cubre solo el CUERPO (filas 13-84). k='v' valor fijo,
 * k='f' fórmula (los 3 subtotales), k='lk' monto por código (`code`/`moneda`).
 * Estilo en `s`, ver ANEXO6_ESTILOS.
 */

export interface EstiloA6 {
  font: { name: string; size: number; bold?: boolean; color?: string };
  fill?: string;
  border?: { t?: string; b?: string; l?: string; r?: string };
  numFmt?: string;
  align?: { h?: string; v?: string; wrap?: boolean; indent?: number };
}

export interface CeldaAnexo6 {
  ref: string;
  k: 'v' | 'f' | 'lk';
  v?: string;
  f?: string;
  code?: string;
  moneda?: 'MN' | 'ME';
  s: string;
}

// Anchos de columna (A:F), tomados del formato oficial.
export const ANEXO6_COLS: { [col: string]: number } = {
  A: 46.7,
  B: 11.6,
  C: 30.0,
  D: 26.9,
  E: 20.6,
  F: 28.9,
};

// Catálogo de estilos (fuente, relleno, bordes, formato, alineación) usados en el cuerpo de la tabla.
export const ANEXO6_ESTILOS: { [key: string]: EstiloA6 } = {
  s1: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, numFmt: "#,##0.00", align: { h: "right" } },
  s2: { font: { name: "Aptos Narrow", size: 11.0 }, numFmt: "#,##0.00", align: { h: "right" } },
  // Nota sobre `indent`: verificado celda por celda contra el Excel de
  // referencia (Aleema_Chorotega.xlsx, hoja Anexo6_ER): las cuentas "nivel 1"
  // (categorías, ej. PRODUCTOS FINANCIEROS) llevan indent=1 y las subcuentas
  // debajo de ellas (ej. Sobre Disponibilidades) llevan indent=2.
  s13: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { t: "thin", l: "thin", r: "thin" }, align: { h: "left", v: "center", indent: 1 } },
  s14: { font: { name: "Aptos Narrow", size: 11.0, color: "FFBFBFBF" }, border: { l: "thin", r: "thin" }, align: { h: "left" } },
  s15: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { l: "thin", r: "thin" }, numFmt: "#,##0.00", align: { h: "right" } },
  s16: { font: { name: "Aptos Narrow", size: 11.0 }, border: { t: "thin", l: "thin", r: "thin" }, numFmt: "#,##0.00", align: { h: "right" } },
  s17: { font: { name: "Aptos Narrow", size: 11.0 }, border: { t: "thin", l: "thin", r: "thin" }, align: { h: "left" } },
  s18: { font: { name: "Aptos Narrow", size: 11.0 }, border: { l: "thin", r: "thin" }, align: { h: "left", v: "center", indent: 2 } },
  // s18b: igual que s18 pero en negrita, para los nombres de cuenta "nivel 1"
  // (categorías principales: INGRESOS, PRODUCTOS FINANCIEROS, COMISIONES, etc.)
  s18b: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { l: "thin", r: "thin" }, align: { h: "left", v: "center", indent: 1 } },
  // s18b2: igual que s18b (negrita) pero con la sangría de subcuenta (indent 2).
  // Se usa solo para "COMISIONES" (fila 38, bajo GASTOS FINANCIEROS), que en el
  // Excel oficial va con negrita de categoría pero con la sangría de detalle.
  s18b2: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { l: "thin", r: "thin" }, align: { h: "left", v: "center", indent: 2 } },
  s19: { font: { name: "Aptos Narrow", size: 11.0 }, border: { l: "thin", r: "thin" }, numFmt: "#,##0.00", align: { h: "right" } },
  s20: { font: { name: "Aptos Narrow", size: 11.0 }, border: { b: "thin", l: "thin", r: "thin" }, numFmt: "#,##0.00", align: { h: "right" } },
  s21: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { b: "thin", l: "thin", r: "thin" }, align: { h: "left", v: "center", indent: 1 } },
  s22: { font: { name: "Aptos Narrow", size: 11.0, color: "FFBFBFBF" }, border: { b: "thin", l: "thin", r: "thin" }, align: { h: "left" } },
  s23: { font: { name: "Aptos Narrow", size: 11.0, bold: true }, border: { b: "thin", l: "thin", r: "thin" }, numFmt: "#,##0.00", align: { h: "right" } },
};

// Volcado celda por celda del cuerpo de la tabla oficial (A13:F84).
export const ANEXO6_CELDAS: CeldaAnexo6[] = [
  { ref: "A13", k: 'v', v: "INGRESOS", s: "s13" },
  { ref: "B13", k: 'v', v: "5", s: "s14" },
  { ref: "C13", k: 'lk', code: "5", moneda: "MN", s: "s15" },
  { ref: "D13", k: 'v', v: "", s: "s16" },
  { ref: "E13", k: 'v', v: "", s: "s17" },
  { ref: "F13", k: 'v', v: "", s: "s16" },
  { ref: "A14", k: 'v', v: "PRODUCTOS FINANCIEROS", s: "s18b" },
  { ref: "B14", k: 'v', v: "501", s: "s14" },
  { ref: "C14", k: 'lk', code: "501", moneda: "MN", s: "s15" },
  { ref: "D14", k: 'v', v: "", s: "s2" },
  { ref: "E14", k: 'v', v: "", s: "s14" },
  { ref: "F14", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A15", k: 'v', v: "", s: "s18" },
  { ref: "B15", k: 'v', v: "", s: "s14" },
  { ref: "C15", k: 'v', v: "", s: "s15" },
  { ref: "D15", k: 'lk', code: "", moneda: "MN", s: "s20" },
  { ref: "E15", k: 'v', v: "", s: "s14" },
  { ref: "F15", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A16", k: 'v', v: "INTERESES", s: "s18b" },
  { ref: "B16", k: 'v', v: "501101", s: "s14" },
  { ref: "C16", k: 'v', v: "", s: "s15" },
  { ref: "D16", k: 'lk', code: "501101", moneda: "MN", s: "s19" },
  { ref: "E16", k: 'v', v: "501201", s: "s14" },
  { ref: "F16", k: 'lk', code: "501201", moneda: "ME", s: "s19" },
  { ref: "A17", k: 'v', v: "Sobre Disponibilidades", s: "s18" },
  { ref: "B17", k: 'v', v: "501101.01", s: "s14" },
  { ref: "C17", k: 'v', v: "", s: "s15" },
  { ref: "D17", k: 'lk', code: "501101.01", moneda: "MN", s: "s19" },
  { ref: "E17", k: 'v', v: "501201.01", s: "s14" },
  { ref: "F17", k: 'lk', code: "501201.01", moneda: "ME", s: "s19" },
  { ref: "A18", k: 'v', v: "Sobre Inversiones", s: "s18" },
  { ref: "B18", k: 'v', v: "501101.02", s: "s14" },
  { ref: "C18", k: 'v', v: "", s: "s15" },
  { ref: "D18", k: 'lk', code: "501101.02", moneda: "MN", s: "s19" },
  { ref: "E18", k: 'v', v: "501201.02", s: "s14" },
  { ref: "F18", k: 'lk', code: "501201.02", moneda: "ME", s: "s19" },
  { ref: "A19", k: 'v', v: "Sobre Cartera de Préstamos", s: "s18" },
  { ref: "B19", k: 'v', v: "501101.03", s: "s14" },
  { ref: "C19", k: 'v', v: "", s: "s15" },
  { ref: "D19", k: 'lk', code: "501101.03", moneda: "MN", s: "s19" },
  { ref: "E19", k: 'v', v: "501201.03", s: "s14" },
  { ref: "F19", k: 'lk', code: "501201.03", moneda: "ME", s: "s19" },
  { ref: "A20", k: 'v', v: "COMISIONES", s: "s18b" },
  { ref: "B20", k: 'v', v: "501103", s: "s14" },
  { ref: "C20", k: 'v', v: "", s: "s15" },
  { ref: "D20", k: 'lk', code: "501103", moneda: "MN", s: "s19" },
  { ref: "E20", k: 'v', v: "501203", s: "s14" },
  { ref: "F20", k: 'lk', code: "501203", moneda: "ME", s: "s19" },
  { ref: "A21", k: 'v', v: "Sobre Cartera Crediticia", s: "s18" },
  { ref: "B21", k: 'v', v: "501103.01", s: "s14" },
  { ref: "C21", k: 'v', v: "", s: "s15" },
  { ref: "D21", k: 'lk', code: "501103.01", moneda: "MN", s: "s19" },
  { ref: "E21", k: 'v', v: "501203.01", s: "s14" },
  { ref: "F21", k: 'lk', code: "501203.01", moneda: "ME", s: "s19" },
  { ref: "A22", k: 'v', v: "Comisiones Varias", s: "s18" },
  { ref: "B22", k: 'v', v: "501103.99", s: "s14" },
  { ref: "C22", k: 'v', v: "", s: "s15" },
  { ref: "D22", k: 'lk', code: "501103.99", moneda: "MN", s: "s19" },
  { ref: "E22", k: 'v', v: "501203.99", s: "s14" },
  { ref: "F22", k: 'lk', code: "501203.99", moneda: "ME", s: "s19" },
  { ref: "A23", k: 'v', v: "OTROS PRODUCTOS", s: "s18b" },
  { ref: "B23", k: 'v', v: "501105", s: "s14" },
  { ref: "C23", k: 'v', v: "", s: "s15" },
  { ref: "D23", k: 'lk', code: "501105", moneda: "MN", s: "s19" },
  { ref: "E23", k: 'v', v: "", s: "s14" },
  { ref: "F23", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A24", k: 'v', v: "Fluctuaciones de Tipo de Cambio", s: "s18" },
  { ref: "B24", k: 'v', v: "501105.01", s: "s14" },
  { ref: "C24", k: 'v', v: "", s: "s15" },
  { ref: "D24", k: 'lk', code: "501105.01", moneda: "MN", s: "s19" },
  { ref: "E24", k: 'v', v: "", s: "s14" },
  { ref: "F24", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A25", k: 'v', v: "Otros Ingresos Financieros", s: "s18b" },
  { ref: "B25", k: 'v', v: "501199", s: "s14" },
  { ref: "C25", k: 'v', v: "", s: "s15" },
  { ref: "D25", k: 'lk', code: "501199", moneda: "MN", s: "s19" },
  { ref: "E25", k: 'v', v: "501299", s: "s14" },
  { ref: "F25", k: 'lk', code: "501299", moneda: "ME", s: "s19" },
  { ref: "A26", k: 'v', v: "Otros Ingresos", s: "s18" },
  { ref: "B26", k: 'v', v: "501199.99", s: "s14" },
  { ref: "C26", k: 'v', v: "", s: "s15" },
  { ref: "D26", k: 'lk', code: "501199.99", moneda: "MN", s: "s19" },
  { ref: "E26", k: 'v', v: "501299.99", s: "s14" },
  { ref: "F26", k: 'lk', code: "501299.99", moneda: "ME", s: "s19" },
  { ref: "A27", k: 'v', v: "", s: "s18" },
  { ref: "B27", k: 'v', v: "", s: "s14" },
  { ref: "C27", k: 'v', v: "", s: "s15" },
  { ref: "D27", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E27", k: 'v', v: "", s: "s14" },
  { ref: "F27", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A28", k: 'v', v: "GASTOS FINANCIEROS", s: "s18b" },
  { ref: "B28", k: 'v', v: "6011", s: "s14" },
  { ref: "C28", k: 'lk', code: "6011", moneda: "MN", s: "s15" },
  { ref: "D28", k: 'lk', code: "6011", moneda: "MN", s: "s19" },
  { ref: "E28", k: 'v', v: "6012", s: "s14" },
  { ref: "F28", k: 'lk', code: "6012", moneda: "ME", s: "s19" },
  { ref: "A29", k: 'v', v: "", s: "s18" },
  { ref: "B29", k: 'v', v: "", s: "s14" },
  { ref: "C29", k: 'v', v: "", s: "s15" },
  { ref: "D29", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E29", k: 'v', v: "", s: "s14" },
  { ref: "F29", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A30", k: 'v', v: "INTERESES", s: "s18b" },
  { ref: "B30", k: 'v', v: "601101", s: "s14" },
  { ref: "C30", k: 'v', v: "", s: "s15" },
  { ref: "D30", k: 'lk', code: "601101", moneda: "MN", s: "s19" },
  { ref: "E30", k: 'v', v: "601201", s: "s14" },
  { ref: "F30", k: 'lk', code: "601201", moneda: "ME", s: "s19" },
  { ref: "A31", k: 'v', v: "Depósitos de Ahorro", s: "s18" },
  { ref: "B31", k: 'v', v: "601101.02", s: "s14" },
  { ref: "C31", k: 'v', v: "", s: "s15" },
  { ref: "D31", k: 'lk', code: "601101.02", moneda: "MN", s: "s19" },
  { ref: "E31", k: 'v', v: "601201.02", s: "s14" },
  { ref: "F31", k: 'lk', code: "601201.02", moneda: "ME", s: "s19" },
  { ref: "A32", k: 'v', v: "Depósitos a Plazo", s: "s18" },
  { ref: "B32", k: 'v', v: "601101.03", s: "s14" },
  { ref: "C32", k: 'v', v: "", s: "s15" },
  { ref: "D32", k: 'lk', code: "601101.03", moneda: "MN", s: "s19" },
  { ref: "E32", k: 'v', v: "601201.03", s: "s14" },
  { ref: "F32", k: 'lk', code: "601201.03", moneda: "ME", s: "s19" },
  { ref: "A33", k: 'v', v: "Préstamos de Instituciones Financieras Nacionales", s: "s18" },
  { ref: "B33", k: 'v', v: "601101.08", s: "s14" },
  { ref: "C33", k: 'v', v: "", s: "s15" },
  { ref: "D33", k: 'lk', code: "601101.08", moneda: "MN", s: "s19" },
  { ref: "E33", k: 'v', v: "601201.08", s: "s14" },
  { ref: "F33", k: 'lk', code: "601201.08", moneda: "ME", s: "s19" },
  { ref: "A34", k: 'v', v: "Préstamos Redescontados", s: "s18" },
  { ref: "B34", k: 'v', v: "601101.13", s: "s14" },
  { ref: "C34", k: 'v', v: "", s: "s15" },
  { ref: "D34", k: 'lk', code: "601101.13", moneda: "MN", s: "s19" },
  { ref: "E34", k: 'v', v: "601201.13", s: "s14" },
  { ref: "F34", k: 'lk', code: "601201.13", moneda: "ME", s: "s19" },
  { ref: "A35", k: 'v', v: "Préstamos de Instituciones Internacionales", s: "s18" },
  { ref: "B35", k: 'v', v: "601101.14", s: "s14" },
  { ref: "C35", k: 'v', v: "", s: "s15" },
  { ref: "D35", k: 'lk', code: "601101.14", moneda: "MN", s: "s19" },
  { ref: "E35", k: 'v', v: "601201.14", s: "s14" },
  { ref: "F35", k: 'lk', code: "601201.14", moneda: "ME", s: "s19" },
  { ref: "A36", k: 'v', v: "Aportaciones", s: "s18" },
  { ref: "B36", k: 'v', v: "601101.15", s: "s14" },
  { ref: "C36", k: 'v', v: "", s: "s15" },
  { ref: "D36", k: 'lk', code: "601101.15", moneda: "MN", s: "s19" },
  { ref: "E36", k: 'v', v: "601201.15", s: "s14" },
  { ref: "F36", k: 'lk', code: "601201.15", moneda: "ME", s: "s19" },
  { ref: "A37", k: 'v', v: "Otras Obligaciones", s: "s18" },
  { ref: "B37", k: 'v', v: "601101.99", s: "s14" },
  { ref: "C37", k: 'v', v: "", s: "s15" },
  { ref: "D37", k: 'lk', code: "601101.99", moneda: "MN", s: "s19" },
  { ref: "E37", k: 'v', v: "601201.99", s: "s14" },
  { ref: "F37", k: 'lk', code: "601201.99", moneda: "ME", s: "s19" },
  { ref: "A38", k: 'v', v: "COMISIONES", s: "s18b2" },
  { ref: "B38", k: 'v', v: "601102", s: "s14" },
  { ref: "C38", k: 'v', v: "", s: "s15" },
  { ref: "D38", k: 'lk', code: "601102", moneda: "MN", s: "s19" },
  { ref: "E38", k: 'v', v: "601202", s: "s14" },
  { ref: "F38", k: 'lk', code: "601202", moneda: "ME", s: "s19" },
  { ref: "A39", k: 'v', v: "Préstamos de Instituciones Financieras Nacionales", s: "s18" },
  { ref: "B39", k: 'v', v: "601102.02", s: "s14" },
  { ref: "C39", k: 'v', v: "", s: "s15" },
  { ref: "D39", k: 'lk', code: "601102.02", moneda: "MN", s: "s19" },
  { ref: "E39", k: 'v', v: "601202.02", s: "s14" },
  { ref: "F39", k: 'lk', code: "601202.02", moneda: "ME", s: "s19" },
  { ref: "A40", k: 'v', v: "Préstamos de Instituciones Internacionales", s: "s18" },
  { ref: "B40", k: 'v', v: "601102.96", s: "s14" },
  { ref: "C40", k: 'v', v: "", s: "s15" },
  { ref: "D40", k: 'lk', code: "601102.96", moneda: "MN", s: "s19" },
  { ref: "E40", k: 'v', v: "601202.96", s: "s14" },
  { ref: "F40", k: 'lk', code: "601202.96", moneda: "ME", s: "s19" },
  { ref: "A41", k: 'v', v: "Otras Comisiones", s: "s18" },
  { ref: "B41", k: 'v', v: "601102.99", s: "s14" },
  { ref: "C41", k: 'v', v: "", s: "s15" },
  { ref: "D41", k: 'lk', code: "601102.99", moneda: "MN", s: "s19" },
  { ref: "E41", k: 'v', v: "601202.99", s: "s14" },
  { ref: "F41", k: 'lk', code: "601202.99", moneda: "ME", s: "s19" },
  { ref: "A42", k: 'v', v: "OTROS GASTOS", s: "s18b" },
  { ref: "B42", k: 'v', v: "601105", s: "s14" },
  { ref: "C42", k: 'v', v: "", s: "s15" },
  { ref: "D42", k: 'lk', code: "601105", moneda: "MN", s: "s19" },
  { ref: "E42", k: 'v', v: "", s: "s14" },
  { ref: "F42", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A43", k: 'v', v: "Fluctuaciones de Tipo de Cambio", s: "s18" },
  { ref: "B43", k: 'v', v: "601105.01", s: "s14" },
  { ref: "C43", k: 'v', v: "", s: "s15" },
  { ref: "D43", k: 'lk', code: "601105.01", moneda: "MN", s: "s19" },
  { ref: "E43", k: 'v', v: "", s: "s14" },
  { ref: "F43", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A44", k: 'v', v: "OTROS GASTOS FINANCIEROS", s: "s18b" },
  { ref: "B44", k: 'v', v: "601199", s: "s14" },
  { ref: "C44", k: 'v', v: "", s: "s15" },
  { ref: "D44", k: 'lk', code: "601199", moneda: "MN", s: "s19" },
  { ref: "E44", k: 'v', v: "", s: "s14" },
  { ref: "F44", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A45", k: 'v', v: "Otros Gastos", s: "s18" },
  { ref: "B45", k: 'v', v: "601199.99", s: "s14" },
  { ref: "C45", k: 'v', v: "", s: "s15" },
  { ref: "D45", k: 'lk', code: "601199.99", moneda: "MN", s: "s19" },
  { ref: "E45", k: 'v', v: "", s: "s14" },
  { ref: "F45", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A46", k: 'v', v: "", s: "s18" },
  { ref: "B46", k: 'v', v: "", s: "s14" },
  { ref: "C46", k: 'v', v: "", s: "s15" },
  { ref: "D46", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E46", k: 'v', v: "", s: "s14" },
  { ref: "F46", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A47", k: 'v', v: "RESULTADOS FINANCIEROS", s: "s21" },
  { ref: "B47", k: 'v', v: "", s: "s22" },
  { ref: "C47", k: 'f', f: "C14-C28", s: "s23" },
  { ref: "D47", k: 'lk', code: "", moneda: "MN", s: "s20" },
  { ref: "E47", k: 'v', v: "", s: "s22" },
  { ref: "F47", k: 'lk', code: "", moneda: "ME", s: "s20" },
  { ref: "A48", k: 'v', v: "", s: "s18" },
  { ref: "B48", k: 'v', v: "", s: "s14" },
  { ref: "C48", k: 'v', v: "", s: "s15" },
  { ref: "D48", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E48", k: 'v', v: "", s: "s14" },
  { ref: "F48", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A49", k: 'v', v: "PRODUCTOS POR SERVICIOS", s: "s18b" },
  { ref: "B49", k: 'v', v: "502", s: "s14" },
  { ref: "C49", k: 'lk', code: "502", moneda: "MN", s: "s15" },
  { ref: "D49", k: 'v', v: "", s: "s19" },
  { ref: "E49", k: 'v', v: "", s: "s14" },
  { ref: "F49", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A50", k: 'v', v: "COMISIONES", s: "s18b" },
  { ref: "B50", k: 'v', v: "502101", s: "s14" },
  { ref: "C50", k: 'v', v: "", s: "s15" },
  { ref: "D50", k: 'lk', code: "502101", moneda: "MN", s: "s19" },
  { ref: "E50", k: 'v', v: "502201", s: "s14" },
  { ref: "F50", k: 'lk', code: "502201", moneda: "ME", s: "s19" },
  { ref: "A51", k: 'v', v: "Remesas", s: "s18" },
  { ref: "B51", k: 'v', v: "502101.01", s: "s14" },
  { ref: "C51", k: 'v', v: "", s: "s15" },
  { ref: "D51", k: 'lk', code: "502101.01", moneda: "MN", s: "s19" },
  { ref: "E51", k: 'v', v: "502201.01", s: "s14" },
  { ref: "F51", k: 'lk', code: "502201.01", moneda: "ME", s: "s19" },
  { ref: "A52", k: 'v', v: "Cobranzas por Cuenta Ajena", s: "s18" },
  { ref: "B52", k: 'v', v: "502101.02", s: "s14" },
  { ref: "C52", k: 'v', v: "", s: "s15" },
  { ref: "D52", k: 'lk', code: "502101.02", moneda: "MN", s: "s19" },
  { ref: "E52", k: 'v', v: "502201.02", s: "s14" },
  { ref: "F52", k: 'lk', code: "502201.02", moneda: "ME", s: "s19" },
  { ref: "A53", k: 'v', v: "Otras Comisiones", s: "s18" },
  { ref: "B53", k: 'v', v: "502101.99", s: "s14" },
  { ref: "C53", k: 'v', v: "", s: "s15" },
  { ref: "D53", k: 'lk', code: "502101.99", moneda: "MN", s: "s19" },
  { ref: "E53", k: 'v', v: "502201.99", s: "s14" },
  { ref: "F53", k: 'lk', code: "502201.99", moneda: "ME", s: "s19" },
  { ref: "A54", k: 'v', v: "ARRENDAMIENTOS", s: "s18b" },
  { ref: "B54", k: 'v', v: "502102", s: "s14" },
  { ref: "C54", k: 'v', v: "", s: "s15" },
  { ref: "D54", k: 'lk', code: "502102", moneda: "MN", s: "s19" },
  { ref: "E54", k: 'v', v: "", s: "s14" },
  { ref: "F54", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A55", k: 'v', v: "Alquiler de Activos Fijos ", s: "s18" },
  { ref: "B55", k: 'v', v: "502102.01", s: "s14" },
  { ref: "C55", k: 'v', v: "", s: "s15" },
  { ref: "D55", k: 'lk', code: "502102.01", moneda: "MN", s: "s19" },
  { ref: "E55", k: 'v', v: "", s: "s14" },
  { ref: "F55", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A56", k: 'v', v: "OTROS INGRESOS POR SERVICIOS", s: "s18b" },
  { ref: "B56", k: 'v', v: "502103", s: "s14" },
  { ref: "C56", k: 'v', v: "", s: "s15" },
  { ref: "D56", k: 'lk', code: "502103", moneda: "MN", s: "s19" },
  { ref: "E56", k: 'v', v: "", s: "s14" },
  { ref: "F56", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A57", k: 'v', v: "Cuota de Ingreso de Afiliados", s: "s18" },
  { ref: "B57", k: 'v', v: "502103.01", s: "s14" },
  { ref: "C57", k: 'v', v: "", s: "s15" },
  { ref: "D57", k: 'lk', code: "502103.01", moneda: "MN", s: "s19" },
  { ref: "E57", k: 'v', v: "", s: "s14" },
  { ref: "F57", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A58", k: 'v', v: "Otros Ingresos por Servicios", s: "s18" },
  { ref: "B58", k: 'v', v: "502103.99", s: "s14" },
  { ref: "C58", k: 'v', v: "", s: "s15" },
  { ref: "D58", k: 'lk', code: "502103.99", moneda: "MN", s: "s19" },
  { ref: "E58", k: 'v', v: "", s: "s14" },
  { ref: "F58", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A59", k: 'v', v: "", s: "s18" },
  { ref: "B59", k: 'v', v: "", s: "s14" },
  { ref: "C59", k: 'v', v: "", s: "s15" },
  { ref: "D59", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E59", k: 'v', v: "", s: "s14" },
  { ref: "F59", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A60", k: 'v', v: "GASTOS DE ADMINISTRACION", s: "s18b" },
  { ref: "B60", k: 'v', v: "602", s: "s14" },
  { ref: "C60", k: 'lk', code: "602", moneda: "MN", s: "s15" },
  { ref: "D60", k: 'v', v: "", s: "s2" },
  { ref: "E60", k: 'v', v: "", s: "s14" },
  { ref: "F60", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A61", k: 'v', v: "", s: "s18" },
  { ref: "B61", k: 'v', v: "", s: "s14" },
  { ref: "C61", k: 'v', v: "", s: "s15" },
  { ref: "D61", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E61", k: 'v', v: "", s: "s14" },
  { ref: "F61", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A62", k: 'v', v: "RESULTADO DE OPERACIÓN ", s: "s18b" },
  { ref: "B62", k: 'v', v: "", s: "s14" },
  { ref: "C62", k: 'f', f: "C47+C49-C60", s: "s15" },
  { ref: "D62", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E62", k: 'v', v: "", s: "s14" },
  { ref: "F62", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A63", k: 'v', v: "", s: "s18" },
  { ref: "B63", k: 'v', v: "", s: "s14" },
  { ref: "C63", k: 'v', v: "", s: "s15" },
  { ref: "D63", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E63", k: 'v', v: "", s: "s14" },
  { ref: "F63", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A64", k: 'v', v: "OTROS PRODUCTOS", s: "s18b" },
  { ref: "B64", k: 'v', v: "503", s: "s14" },
  { ref: "C64", k: 'lk', code: "503", moneda: "MN", s: "s15" },
  { ref: "D64", k: 'v', v: "", s: "s2" },
  { ref: "E64", k: 'v', v: "", s: "s14" },
  { ref: "F64", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A65", k: 'v', v: "Venta de Activos Eventuales", s: "s18" },
  { ref: "B65", k: 'v', v: "503101", s: "s14" },
  { ref: "C65", k: 'v', v: "", s: "s15" },
  { ref: "D65", k: 'lk', code: "503101", moneda: "MN", s: "s19" },
  { ref: "E65", k: 'v', v: "", s: "s14" },
  { ref: "F65", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A66", k: 'v', v: "Ventas de Bienes Muebles e Inmuebles", s: "s18" },
  { ref: "B66", k: 'v', v: "503102", s: "s14" },
  { ref: "C66", k: 'v', v: "", s: "s15" },
  { ref: "D66", k: 'lk', code: "503102", moneda: "MN", s: "s19" },
  { ref: "E66", k: 'v', v: "", s: "s14" },
  { ref: "F66", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A67", k: 'v', v: "Donaciones Recibidas", s: "s18" },
  { ref: "B67", k: 'v', v: "503103", s: "s14" },
  { ref: "C67", k: 'v', v: "", s: "s15" },
  { ref: "D67", k: 'lk', code: "503103", moneda: "MN", s: "s19" },
  { ref: "E67", k: 'v', v: "", s: "s14" },
  { ref: "F67", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A68", k: 'v', v: "Intereses Cobrados sobre Operaciones Readecuadas", s: "s18" },
  { ref: "B68", k: 'v', v: "503104", s: "s14" },
  { ref: "C68", k: 'v', v: "", s: "s15" },
  { ref: "D68", k: 'lk', code: "503104", moneda: "MN", s: "s19" },
  { ref: "E68", k: 'v', v: "", s: "s14" },
  { ref: "F68", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A69", k: 'v', v: "Donaciones Recibidas", s: "s18" },
  { ref: "B69", k: 'v', v: "503198", s: "s14" },
  { ref: "C69", k: 'v', v: "", s: "s15" },
  { ref: "D69", k: 'lk', code: "503198", moneda: "MN", s: "s19" },
  { ref: "E69", k: 'v', v: "", s: "s14" },
  { ref: "F69", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A70", k: 'v', v: "Otros Productos", s: "s18" },
  { ref: "B70", k: 'v', v: "503199", s: "s14" },
  { ref: "C70", k: 'v', v: "", s: "s15" },
  { ref: "D70", k: 'lk', code: "503199", moneda: "MN", s: "s19" },
  { ref: "E70", k: 'v', v: "", s: "s14" },
  { ref: "F70", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A71", k: 'v', v: "", s: "s18" },
  { ref: "B71", k: 'v', v: "", s: "s14" },
  { ref: "C71", k: 'v', v: "", s: "s15" },
  { ref: "D71", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E71", k: 'v', v: "", s: "s14" },
  { ref: "F71", k: 'v', v: "", s: "s19" },
  { ref: "A72", k: 'v', v: "OTROS GASTOS", s: "s18b" },
  { ref: "B72", k: 'v', v: "697", s: "s14" },
  { ref: "C72", k: 'lk', code: "697", moneda: "MN", s: "s15" },
  { ref: "D72", k: 'v', v: "", s: "s19" },
  { ref: "E72", k: 'v', v: "", s: "s14" },
  { ref: "F72", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A73", k: 'v', v: "Venta de Activos Eventuales", s: "s18" },
  { ref: "B73", k: 'v', v: "697101", s: "s14" },
  { ref: "C73", k: 'v', v: "", s: "s15" },
  { ref: "D73", k: 'lk', code: "697101", moneda: "MN", s: "s19" },
  { ref: "E73", k: 'v', v: "", s: "s14" },
  { ref: "F73", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A74", k: 'v', v: "Ventas de Bienes Muebles e Inmuebles", s: "s18" },
  { ref: "B74", k: 'v', v: "697102", s: "s14" },
  { ref: "C74", k: 'v', v: "", s: "s15" },
  { ref: "D74", k: 'lk', code: "697102", moneda: "MN", s: "s19" },
  { ref: "E74", k: 'v', v: "", s: "s14" },
  { ref: "F74", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A75", k: 'v', v: "Sanciones, Multas e Indemnizaciones", s: "s18" },
  { ref: "B75", k: 'v', v: "697103", s: "s14" },
  { ref: "C75", k: 'v', v: "", s: "s15" },
  { ref: "D75", k: 'lk', code: "697103", moneda: "MN", s: "s19" },
  { ref: "E75", k: 'v', v: "", s: "s14" },
  { ref: "F75", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A76", k: 'v', v: "Otros Gastos", s: "s18" },
  { ref: "B76", k: 'v', v: "697199", s: "s14" },
  { ref: "C76", k: 'v', v: "", s: "s15" },
  { ref: "D76", k: 'lk', code: "697199", moneda: "MN", s: "s19" },
  { ref: "E76", k: 'v', v: "", s: "s14" },
  { ref: "F76", k: 'v', v: "", s: "s19" },
  { ref: "A77", k: 'v', v: "", s: "s18" },
  { ref: "B77", k: 'v', v: "", s: "s14" },
  { ref: "C77", k: 'v', v: "", s: "s15" },
  { ref: "D77", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E77", k: 'v', v: "", s: "s14" },
  { ref: "F77", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A78", k: 'v', v: "INGRESOS DE EJERCICIOS ANTERIORES", s: "s18b" },
  { ref: "B78", k: 'v', v: "599", s: "s14" },
  { ref: "C78", k: 'lk', code: "599", moneda: "MN", s: "s15" },
  { ref: "D78", k: 'v', v: "", s: "s19" },
  { ref: "E78", k: 'v', v: "", s: "s14" },
  { ref: "F78", k: 'v', v: "", s: "s19" },
  { ref: "A79", k: 'v', v: "Recuperaciones", s: "s18" },
  { ref: "B79", k: 'v', v: "599102", s: "s14" },
  { ref: "C79", k: 'v', v: "", s: "s15" },
  { ref: "D79", k: 'lk', code: "599102", moneda: "MN", s: "s19" },
  { ref: "E79", k: 'v', v: "", s: "s14" },
  { ref: "F79", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A80", k: 'v', v: "Otros Ingresos de Ejercicios Anteriores", s: "s18" },
  { ref: "B80", k: 'v', v: "599199", s: "s14" },
  { ref: "C80", k: 'v', v: "", s: "s15" },
  { ref: "D80", k: 'lk', code: "599199", moneda: "MN", s: "s19" },
  { ref: "E80", k: 'v', v: "", s: "s14" },
  { ref: "F80", k: 'lk', code: "", moneda: "ME", s: "s19" },
  // A81/A82: por pedido explícito, estas dos cuentas van sin negrita (a
  // diferencia del resto de categorías "nivel 1" que sí conservan s18b).
  { ref: "A81", k: 'v', v: "GASTOS DE EJERCICIOS ANTERIORES", s: "s18" },
  { ref: "B81", k: 'v', v: "699", s: "s14" },
  { ref: "C81", k: 'lk', code: "699", moneda: "MN", s: "s15" },
  { ref: "D81", k: 'v', v: "", s: "s2" },
  { ref: "E81", k: 'v', v: "", s: "s14" },
  { ref: "F81", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A82", k: 'v', v: "Gastos de Ejercicios Anteriores", s: "s18" },
  { ref: "B82", k: 'v', v: "699101", s: "s14" },
  { ref: "C82", k: 'v', v: "", s: "s1" },
  { ref: "D82", k: 'lk', code: "699101", moneda: "MN", s: "s15" },
  { ref: "E82", k: 'v', v: "", s: "s14" },
  { ref: "F82", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A83", k: 'v', v: "", s: "s18" },
  { ref: "B83", k: 'v', v: "", s: "s14" },
  { ref: "C83", k: 'v', v: "", s: "s15" },
  { ref: "D83", k: 'lk', code: "", moneda: "MN", s: "s19" },
  { ref: "E83", k: 'v', v: "", s: "s14" },
  { ref: "F83", k: 'lk', code: "", moneda: "ME", s: "s19" },
  { ref: "A84", k: 'v', v: "EXCEDENTES O PÉRDIDAS DEL EJERCICIO", s: "s21" },
  { ref: "B84", k: 'v', v: "", s: "s22" },
  { ref: "C84", k: 'f', f: "C62+C64+C78-C72-C81", s: "s23" },
  { ref: "D84", k: 'v', v: "", s: "s20" },
  { ref: "E84", k: 'v', v: "", s: "s22" },
  { ref: "F84", k: 'lk', code: "", moneda: "ME", s: "s20" },
];
